import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Grid, IconButton, MenuItem, Stack, TextField, Tooltip, Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import api from "../../api/client";
import { endpoints } from "../../api/endpoints";
import { PageHeader, SectionCard, useConfirm } from "../ui";
import { extractErrorMessage } from "../../utils/errorMessage";
import { resolveExternalUrl } from "../../utils/openNotificationUrl";
import { sortByLabel } from "../../utils/sortOptions";
import { useAuth } from "../../context/AuthContext";

const MONTH_NAMES = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];
const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// SR 203/208: Use local date components to avoid UTC off-by-one in IST and similar timezones
const toLocalDateStr = (date) =>
  [date.getFullYear(), String(date.getMonth() + 1).padStart(2, "0"), String(date.getDate()).padStart(2, "0")].join("-");

const RECURRENCE_OPTS = [
  { v: "none", l: "One-time" },
  { v: "daily", l: "Daily" },
  { v: "weekly", l: "Weekly" },
  { v: "monthly", l: "Monthly" },
];

export default function CalendarViewNew() {
  const confirm = useConfirm();
  const { user } = useAuth();
  const myId = user?.employeeid;
  const [viewDate, setViewDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  // When viewing an event you were only invited to (not the creator), the
  // dialog opens read-only — only the creator can edit/delete.
  const [readOnly, setReadOnly] = useState(false);
  const [subOpen, setSubOpen] = useState(false);
  const [subUrl, setSubUrl] = useState("");

  const openSubscribe = async () => {
    try {
      const res = await api.get("/api/calendar/feed-url");
      setSubUrl(res.data?.data?.url || "");
      setSubOpen(true);
    } catch (err) {
      toast.error("Couldn't generate your calendar link");
    }
  };

  // ---- Google Calendar two-way sync ----
  const [gcal, setGcal] = useState({ configured: false, connected: false, email: null });
  const loadGcalStatus = () => api.get("/api/google/status").then((r) => setGcal(r.data?.data || {})).catch(() => {});
  useEffect(() => {
    loadGcalStatus();
    // Handle the OAuth return flag Google redirected us back with.
    const params = new URLSearchParams(window.location.search);
    const flag = params.get("google");
    if (flag) {
      if (flag === "connected") { toast.success("Google Calendar connected"); loadGcalStatus(); }
      else if (flag === "denied") toast.info("Google connection cancelled");
      else toast.error("Google connection failed — please try again");
      params.delete("google");
      window.history.replaceState({}, "", `${window.location.pathname}${params.toString() ? "?" + params.toString() : ""}`);
    }
  }, []);
  const connectGoogle = async () => {
    try {
      const r = await api.get("/api/google/connect");
      if (r.data?.data?.url) window.location.href = r.data.data.url;
    } catch (err) { toast.error("Couldn't start the Google connection"); }
  };
  const disconnectGoogle = async () => {
    try { await api.post("/api/google/disconnect"); setGcal((s) => ({ ...s, connected: false, email: null })); toast.success("Google Calendar disconnected"); }
    catch (err) { toast.error("Couldn't disconnect"); }
  };

  const [form, setForm] = useState({
    name: "", time: "", endTime: "",
    date: toLocalDateStr(new Date()),
    recurrence: "none", employees: [],
  });
  // SR 204: track clicked date to show event-list dialog before create form
  const [selectedDate, setSelectedDate] = useState(null);
  const [dateDialogOpen, setDateDialogOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [ev, em] = await Promise.all([
        api.get("/api/holidays/logineduser/events"),
        api.get(endpoints.selfService.directory),
      ]);
      setEvents(ev.data?.tasks || []);
      setEmployees(Array.isArray(em.data) ? em.data : em.data?.data || []);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const empOpts = useMemo(
    () => sortByLabel(
      employees
        .filter((e) => e.employeeid !== myId)
        .map((e) => ({ id: e.employeeid, label: `${e.name} (${e.employeeid})` })),
      (o) => o.label
    ),
    [employees, myId]
  );

  // The creator owns the event. Each invited colleague gets their own copy
  // where `createdBy` still points at the original creator. Legacy events with
  // no `createdBy` fall back to matching the copy's `employeeId`.
  const isOwner = (e) => {
    if (!e) return false;
    return e.createdBy ? String(e.createdBy) === String(myId) : String(e.employeeId) === String(myId);
  };

  const inviteIdsFromTask = (task) => {
    const ids = task?.employees?.length ? task.employees : [task?.employeeId].filter(Boolean);
    return uniqueEmpIds(ids).filter((id) => id !== myId);
  };

  function uniqueEmpIds(ids) {
    return [...new Set((ids || []).map(String).filter(Boolean))];
  }

  const monthDays = useMemo(() => {
    const y = viewDate.getFullYear(), m = viewDate.getMonth();
    const firstDay = new Date(y, m, 1).getDay();
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(y, m, d));
    while (cells.length < 42) cells.push(null);
    return cells;
  }, [viewDate]);

  const eventsByDate = useMemo(() => {
    const map = {};
    events.forEach((e) => {
      // SR 203: use local date string so event matching is timezone-consistent
      const key = toLocalDateStr(new Date(e.date));
      (map[key] ||= []).push(e);
    });
    return map;
  }, [events]);

  const openNew = (date) => {
    setEditing(null);
    setReadOnly(false);
    setForm({
      name: "", time: "", endTime: "",
      // SR 203/208: use local date components to avoid UTC timezone off-by-one
      date: date ? toLocalDateStr(date) : toLocalDateStr(new Date()),
      recurrence: "none", employees: [],
      description: "", location: "", meetingLink: "",
    });
    setOpen(true);
  };

  const openEdit = (e) => {
    setEditing(e);
    setReadOnly(!isOwner(e));
    const invited = inviteIdsFromTask(e);
    setForm({
      name: e.name, time: e.time || "", endTime: e.endTime || "",
      date: toLocalDateStr(new Date(e.date)),
      recurrence: e.recurrence || "none",
      employees: invited.map((id) => empOpts.find((o) => o.id === id)).filter(Boolean),
      description: e.description || "", location: e.location || "", meetingLink: e.meetingLink || "",
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name) return toast.error("Event name required");
    try {
      const body = {
        name: form.name, time: form.time, endTime: form.endTime,
        date: form.date, recurrence: form.recurrence,
        // SR 202/209: backend expects `externalEmployeeIds`, not `employees`
        externalEmployeeIds: form.employees.map((e) => e.id),
        description: form.description, location: form.location, meetingLink: form.meetingLink,
      };
      let res;
      if (editing) {
        // SR 205: correct PUT endpoint is /api/holidays/edittask/:id
        res = await api.put(`/api/holidays/edittask/${editing._id}`, body);
      } else {
        res = await api.post("/api/holidays/tasks", body);
      }

      // SR 220: backend returns 207 (partial success) when some employees have conflicts.
      // Show a success toast for the saved portion, then display each conflict warning
      // sequentially with staggered delays so the user sees them one at a time.
      const conflicts = res.data?.conflicts;
      if (Array.isArray(conflicts) && conflicts.length > 0) {
        // SR 220: 207 Multi-Status. If at least one task was saved, it's a partial
        // success; if `tasks` is empty, NOTHING was saved (e.g. the creator or the
        // only invitee is already booked) — show a warning (not a green success) and
        // keep the dialog open so the user can adjust the time instead of losing input.
        const nothingSaved = Array.isArray(res.data?.tasks) && res.data.tasks.length === 0;
        if (nothingSaved) {
          toast.warning(res.data?.message || "No event created — time conflicts");
        } else {
          toast.success(res.data?.message || "Event created with some conflicts");
        }
        conflicts.forEach((c, idx) => {
          setTimeout(() => {
            toast.warning(c.conflict || `Employee ${c.employeeId} has a time conflict`, {
              autoClose: 4000 + idx * 500,
              toastId: `conflict-${c.employeeId}-${idx}`,
            });
          }, idx * 800);
        });
        if (nothingSaved) return; // leave dialog open; nothing changed to reload
      } else {
        toast.success(editing ? "Event updated" : "Event created");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  const del = async (id) => {
    const ok = await confirm({ title: "Delete event?", danger: true, confirmText: "Delete" });
    if (!ok) return;
    try {
      // SR 206: correct DELETE endpoint is /api/holidays/deletetask/:id
      await api.delete(`/api/holidays/deletetask/${id}`);
      toast.success("Event deleted");
      load();
    } catch (err) { toast.error(extractErrorMessage(err)); }
  };

  const isSameDay = (a, b) => a?.toDateString() === b?.toDateString();
  const today = new Date();

  return (
    <Box p={3}>
      <PageHeader
        title="My Calendar"
        subtitle="Your personal schedule — invited colleagues see the same event on their calendar"
        icon={<CalendarMonthOutlinedIcon />}
        actions={[
          <Button key="new" variant="contained" startIcon={<AddIcon />} onClick={() => openNew()}>
            New Event
          </Button>,
        ]}
      />

      <SectionCard>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Stack direction="row" spacing={1} alignItems="center">
            <IconButton onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1))}>
              <ChevronLeftIcon />
            </IconButton>
            <Typography sx={{ fontSize: 18, fontWeight: 700, minWidth: 200, textAlign: "center" }}>
              {MONTH_NAMES[viewDate.getMonth()]} {viewDate.getFullYear()}
            </Typography>
            <IconButton onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1))}>
              <ChevronRightIcon />
            </IconButton>
          </Stack>
          <Stack direction="row" spacing={1}>
            {gcal.configured && (gcal.connected ? (
              <Button size="small" variant="outlined" color="success" onClick={disconnectGoogle}
                title={gcal.email ? `Connected as ${gcal.email} — click to disconnect` : "Disconnect Google"}>
                Google ✓
              </Button>
            ) : (
              <Button size="small" variant="outlined" onClick={connectGoogle}>Connect Google</Button>
            ))}
            <Button size="small" variant="outlined" onClick={openSubscribe}>📅 Subscribe</Button>
            <Button size="small" onClick={() => setViewDate(new Date())}>Today</Button>
          </Stack>
        </Stack>

        {loading ? (
          <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
        ) : (
          <>
            <Grid container spacing={0.5}>
              {DAY_NAMES.map((d) => (
                <Grid item xs={12 / 7} key={d}>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "text.secondary", textAlign: "center", py: 1 }}>
                    {d}
                  </Typography>
                </Grid>
              ))}
              {monthDays.map((date, i) => {
                // SR 203: use local date string so key matches eventsByDate map
                const key = date ? toLocalDateStr(date) : null;
                const dayEvents = date ? eventsByDate[key] || [] : [];
                const isToday = date && isSameDay(date, today);
                return (
                  <Grid item xs={12 / 7} key={i}>
                    <Box
                      // SR 204: clicking a date opens the event-list dialog, not directly the create form
                      onClick={() => {
                        if (!date) return;
                        setSelectedDate(date);
                        setDateDialogOpen(true);
                      }}
                      sx={{
                        minHeight: 80, border: "1px solid", borderColor: isToday ? "primary.main" : "divider",
                        borderRadius: 1, p: 0.75, cursor: date ? "pointer" : "default",
                        bgcolor: date ? (isToday ? "#F4EDFA" : "background.paper") : "action.hover",
                        transition: "all 0.2s", "&:hover": date ? { borderColor: "primary.main" } : {},
                      }}
                    >
                      {date && (
                        <>
                          <Typography sx={{ fontSize: 12, fontWeight: isToday ? 800 : 600, color: isToday ? "primary.main" : "text.primary" }}>
                            {date.getDate()}
                          </Typography>
                          <Stack spacing={0.25} mt={0.5}>
                            {dayEvents.slice(0, 2).map((e) => (
                              // SR 215/216: wrap chip in Tooltip so full name shown on hover; add ellipsis via sx
                              <Tooltip key={e._id} title={e.name} placement="top" arrow>
                                <Chip
                                  label={e.name} size="small"
                                  onClick={(ev) => { ev.stopPropagation(); openEdit(e); }}
                                  sx={{
                                    height: 18, fontSize: 10, bgcolor: "primary.main", color: "#fff",
                                    "& .MuiChip-label": { px: 0.75, overflow: "hidden", textOverflow: "ellipsis", maxWidth: 100 },
                                  }}
                                />
                              </Tooltip>
                            ))}
                            {dayEvents.length > 2 && (
                              <Typography sx={{ fontSize: 10, color: "text.secondary" }}>+{dayEvents.length - 2} more</Typography>
                            )}
                          </Stack>
                        </>
                      )}
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </>
        )}
      </SectionCard>

      {/* SR 204: Date-click dialog — shows events for the selected day, with an Add button */}
      <Dialog open={dateDialogOpen} onClose={() => setDateDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <EventOutlinedIcon fontSize="small" />
            <span>{selectedDate ? `Events — ${selectedDate.toLocaleDateString("en-GB", { dateStyle: "medium" })}` : "Events"}</span>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {selectedDate && (() => {
            const key = toLocalDateStr(selectedDate);
            const dayEvs = eventsByDate[key] || [];
            if (dayEvs.length === 0) {
              return <Typography color="text.secondary" sx={{ py: 1 }}>No events on this day.</Typography>;
            }
            return (
              <Stack spacing={1} sx={{ pt: 1 }}>
                {dayEvs.map((e) => (
                  <Box key={e._id} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", border: "1px solid", borderColor: "divider", borderRadius: 1, px: 1.5, py: 0.75 }}>
                    <Box>
                      <Typography sx={{ fontWeight: 600, fontSize: 13 }}>{e.name}</Typography>
                      {e.time && <Typography sx={{ fontSize: 11, color: "text.secondary" }}>{e.time}{e.endTime ? ` – ${e.endTime}` : ""}</Typography>}
                    </Box>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      {isOwner(e) ? (
                        <>
                          <IconButton size="small" onClick={() => { setDateDialogOpen(false); openEdit(e); }}>
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => { setDateDialogOpen(false); del(e._id); }}>
                            <DeleteOutlineOutlinedIcon fontSize="small" />
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <Chip label="Invited" size="small" variant="outlined" sx={{ height: 20, fontSize: 10 }} />
                          <Tooltip title="View details" arrow>
                            <IconButton size="small" onClick={() => { setDateDialogOpen(false); openEdit(e); }}>
                              <VisibilityOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDateDialogOpen(false)}>Close</Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setDateDialogOpen(false); openNew(selectedDate); }}>
            Add Event
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? (readOnly ? "Event Details" : "Edit Event") : "New Event"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {readOnly && (
              <Chip
                label="You were invited to this event — only the organiser can edit it"
                size="small" color="info" variant="outlined" sx={{ alignSelf: "flex-start" }}
              />
            )}
            {form.meetingLink && (
              <Button variant="contained" fullWidth href={resolveExternalUrl(form.meetingLink)} target="_blank" rel="noopener noreferrer"
                sx={{ background: "linear-gradient(135deg, #6C50BF, #492ba3)" }}>
                🎥 Join meeting
              </Button>
            )}
            <TextField fullWidth label="Event Name *" value={form.name} disabled={readOnly} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
            {/* SR 214: min restricts past-date selection in UI */}
            <TextField
              fullWidth type="date" label="Date" InputLabelProps={{ shrink: true }}
              value={form.date} disabled={readOnly}
              inputProps={{ min: toLocalDateStr(new Date()) }}
              onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
            />
            <Stack direction="row" spacing={1}>
              {/* SR 213: min on start time when date is today restricts past-time selection */}
              <TextField
                fullWidth type="time" label="Start" InputLabelProps={{ shrink: true }}
                value={form.time} disabled={readOnly}
                inputProps={form.date === toLocalDateStr(new Date()) ? { min: new Date().toTimeString().slice(0, 5) } : {}}
                onChange={(e) => setForm((f) => ({ ...f, time: e.target.value }))}
              />
              <TextField fullWidth type="time" label="End" InputLabelProps={{ shrink: true }} value={form.endTime} disabled={readOnly} onChange={(e) => setForm((f) => ({ ...f, endTime: e.target.value }))} />
            </Stack>
            <TextField fullWidth select label="Recurrence" value={form.recurrence} disabled={readOnly} onChange={(e) => setForm((f) => ({ ...f, recurrence: e.target.value }))}>
              {sortByLabel(RECURRENCE_OPTS, (o) => o.l).map((o) => <MenuItem key={o.v} value={o.v}>{o.l}</MenuItem>)}
            </TextField>
            <Autocomplete
              multiple options={empOpts}
              value={form.employees}
              disabled={readOnly} readOnly={readOnly}
              onChange={(_, v) => setForm((f) => ({ ...f, employees: v }))}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderInput={(p) => (
                <TextField
                  {...p}
                  label="Invite colleagues"
                  placeholder="Search employees"
                  helperText={readOnly ? "Colleagues invited to this event." : "You are added automatically. Invited people will see this on their calendar too."}
                />
              )}
            />
            <TextField fullWidth label="Meeting link (Zoom / Google Meet / Teams)" placeholder="https://meet.google.com/…"
              value={form.meetingLink} disabled={readOnly}
              onChange={(e) => setForm((f) => ({ ...f, meetingLink: e.target.value }))} />
            <TextField fullWidth label="Location (optional)" value={form.location} disabled={readOnly}
              onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))} />
            <TextField fullWidth multiline minRows={2} label="Description / agenda (optional)" value={form.description} disabled={readOnly}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions>
          {editing && !readOnly && (
            <Button color="error" startIcon={<DeleteOutlineOutlinedIcon />} onClick={() => { setOpen(false); del(editing._id); }} sx={{ mr: "auto" }}>
              Delete
            </Button>
          )}
          <Button onClick={() => setOpen(false)}>{readOnly ? "Close" : "Cancel"}</Button>
          {!readOnly && (
            <Button variant="contained" onClick={save} disabled={!form.name}>{editing ? "Update" : "Create"}</Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Subscribe (ICS) — add HRMS meetings to any calendar app */}
      <Dialog open={subOpen} onClose={() => setSubOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Subscribe in Google / Outlook / Apple Calendar</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14, mb: 1.5, color: "text.secondary" }}>
            Copy this private link and add it in your calendar app (in Google Calendar: <b>Other calendars → From URL</b>). Your meetings then keep syncing automatically. Keep it private — anyone with the link can see your events.
          </Typography>
          <TextField fullWidth value={subUrl} size="small" InputProps={{ readOnly: true }} onFocus={(e) => e.target.select()} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { navigator.clipboard?.writeText(subUrl); toast.success("Link copied"); }}>Copy link</Button>
          <Button variant="contained" onClick={() => setSubOpen(false)}>Done</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
